/**
 * Safely extracts an error message from an API error response.
 * Handles both string messages and Pydantic validation error objects.
 * 
 * @param {Error} error - The error object from axios
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} - A user-friendly error message
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  
  if (!detail) {
    return fallback;
  }
  
  // If it's already a string, return it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If it's an array (Pydantic validation errors), extract messages
  if (Array.isArray(detail)) {
    const messages = detail.map(err => {
      if (typeof err === 'string') return err;
      if (err?.msg) return err.msg;
      if (err?.message) return err.message;
      return null;
    }).filter(Boolean);
    
    return messages.length > 0 ? messages.join('. ') : fallback;
  }
  
  // If it's an object with a msg property
  if (detail?.msg) {
    return detail.msg;
  }
  
  // If it's an object with a message property
  if (detail?.message) {
    return detail.message;
  }
  
  // Last resort - return fallback
  return fallback;
};
